import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { DialogOverlayComponent } from '../../../components/dialog-overlay/components/dialog-overlay.component';
import { DialogOverlay } from '../../../services/dialog-overlay.services';
import { RequestBodyService } from '../../../services';
import { UserService } from '../../../../core/services/user.service';
import { AccountAttributesEnum } from '../../../enums';
import { CompaniesApiService } from 'src/app/shared/services/api';
import { SearchModelForm } from '../../../../results/search/services/SearchModelForm';

@Component({
    selector: 'app-specialities',
    templateUrl: './specialities.component.html',
    styleUrls: ['./specialities.component.scss'],
    standalone: false
})

export class SpecialitiesComponent implements OnInit, OnChanges {
    @Input() data: SearchModelForm;
    @Output() triggerSearch: EventEmitter<void> = new EventEmitter<void>();
    @Output() keywordsSelection: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild("keywordsDialog") keywordsDialog: DialogOverlayComponent;

    keywords: Observable<any[]>;
    keywords_any: Observable<any[]>;
    keywords_all: Observable<any[]>;
    keywords_none: Observable<any[]>;
    autocompleteItems: any[];
    autocompleteItems_any: any[];
    autocompleteItems_all: any[];
    autocompleteItems_none: any[];
    popularKeysTabSelected: boolean = true;
    popularKeywords: any[] = [];
    popularKeywordsFiltered: any[] = [];

    public specialitiesInput$ = new Subject<any>();
    public specialitiesAnyInput$ = new Subject<any>();
    public specialitiesAllInput$ = new Subject<any>();
    public specialitiesNoneInput$ = new Subject<any>();
    public loading = false;
    public loading_any = false;
    public loading_all = false;
    public loading_none = false;
    specialities = [];
    specialities_any = [];
    specialities_all = [];
    specialities_none = [];
    specialities_popular = [];
    searchType = "basic";
    hasBooleanAttribute: boolean = false;

    constructor(
        private requestBodyService: RequestBodyService,
        private dialogOverlay: DialogOverlay,
        private userService: UserService,
        private companiesApiService: CompaniesApiService,
    ) {}

    ngOnInit(): void {
        this.hasBooleanAttribute = this.userService.hasAccess(AccountAttributesEnum.BOOLEAN);
        this.loadSpecialities('keywords', 'specialitiesInput$', 'loading', 'autocompleteItems', 'specialities');
        this.loadSpecialities('keywords_any', 'specialitiesAnyInput$', 'loading_any', 'autocompleteItems_any', 'specialities_any');
        this.loadSpecialities('keywords_all', 'specialitiesAllInput$', 'loading_all', 'autocompleteItems_all', 'specialities_all');
        this.loadSpecialities('keywords_none', 'specialitiesNoneInput$', 'loading_none', 'autocompleteItems_none', 'specialities_none');
        if (this.isKeywordsSelected() && !this.specialities_popular.length) {
            this.popularKeysTabSelected = false;
        }
        if (!this.popularKeywords?.length) this.loadPopularKeyWords();
    }

    loadPopularKeyWords(): void {
        this.companiesApiService.getPopularKeywords("").pipe(map(rsp => rsp['matchingKeywords'])).pipe(take(1)).subscribe((keywords) => {
            this.popularKeywords = [...keywords];
            this.popularKeywordsFiltered = [...keywords];
        })
    }

    searchPopularKeywords(criteria): void {
        const searchCriteria = (criteria || '').toUpperCase();
        this.popularKeywordsFiltered = this.popularKeywords.filter((keyword) => keyword.toUpperCase().includes(searchCriteria));
    }

    ngOnChanges() {
        this.hasBooleanAttribute = this.userService.hasAccess(AccountAttributesEnum.BOOLEAN);
        this.specialities = this.data.specialties;
        this.specialities_any = this.data.specialties_any;
        this.specialities_all = this.data.specialties_all;
        this.specialities_none = this.data.specialties_none;
        this.specialities_popular = this.data.popular_keywords;
        if ((this.specialities_any?.length || this.specialities_all?.length || this.specialities_none?.length)
            && this.hasBooleanAttribute) {
            this.searchType = "advanced";
        } else {
            this.searchType = "basic";
        }
    }

    public onSelectAll(specialitiesInput, autocompleteItems, specialties, specialities ) {
        this.requestBodyService.checkVIFilterChangeFirstTime();
        this[specialitiesInput].next('');
        if (this[autocompleteItems] && this[autocompleteItems].length > 0) {
            if (this[specialities] && this[specialities].length > 0) {
                this[specialities] = this[specialities].concat(this[autocompleteItems]);
            } else {
                this[specialities] = this[autocompleteItems];
            }
            this.data[specialties] = this[specialities];
        }
        this[autocompleteItems] = [];
    }


    loadSpecialities(keywords: string, specialitiesInput: string, loading: string, autocompleteItems: string, specialities: string) {
        this[keywords] = concat(
            of([]),
            this[specialitiesInput].pipe(
                distinctUntilChanged(),
                tap(() => {
                    this[loading] = true;
                    this[autocompleteItems] = [];
                }),
                switchMap((term: string) =>
                    (term?.length >=2 ? this.companiesApiService.getSpecialities(term.replace('&', '%26')).pipe(map(rsp => rsp['keys'])) : of([]))
                )
            )
        )
        .pipe(
            catchError(() => of([])),
            tap((items: any[]) => {
                this[loading] = false;
                if (items && items.length > 0) {
                    if (this[specialities] && this[specialities].length > 0) {
                        items = items.filter((el) => !this[specialities].includes(el));
                    }
                    this[autocompleteItems] = items;
                } else {
                    this[autocompleteItems] = [];
                }
            })
        )
    }

    clearSearch(event, specialities, specialties) {
        this[specialities] = this[specialities].filter(item => item !== event.label);
        this.data[specialties] = this[specialities];
    }

    onItemAdded(specialties, specialities) {
        this.data[specialties] = this[specialities];
        this.requestBodyService.universalFilterChanged.next(this.data);
        this.requestBodyService.checkVIFilterChangeFirstTime();
        this.keywordsSelection.emit();
    }

    clearAll(specialitiesInput) {
        this[specialitiesInput].next();
    }

    toggleSearchType(event, searchType) {
        event.preventDefault();
        if (searchType == "basic") {
            if (this.specialities_all?.length || this.specialities_none?.length) {
                this.dialogOverlay
                    .show(
                        this.keywordsDialog,
                        "By switching views, information you’ve entered will be cleared.",
                        "location-section"
                    )
                    .then((res) => {
                        this.specialities = this.specialities_any;
                        this.data.specialties = this.specialities_any;
                        this.specialities_any = [];
                        this.specialities_all = [];
                        this.specialities_none = [];
                        this.data.specialties_any = [];
                        this.data.specialties_all = [];
                        this.data.specialties_none = [];
                        this.searchType = searchType;
                    })
                    .catch((err) => { });
                setTimeout(() => {
                    document.querySelector('.dialog_overlay.location-section').scrollIntoView({
                        behavior: 'smooth'
                    });
                })
            } else {
                this.specialities = this.specialities_any;
                this.data.specialties = this.specialities_any;
                this.specialities_any = [];
                this.data.specialties_any = [];
                this.searchType = searchType;
            }
        } else {
            this.specialities_any = this.specialities;
            this.data.specialties_any = this.specialities;
            this.specialities = [];
            this.data.specialties = [];
            this.searchType = searchType;
        }
    }

    toggleTabs(isPopularKeywords: boolean) {
            if (this.isKeywordsSelected()) {
                this.dialogOverlay
                    .show(
                        this.keywordsDialog,
                        isPopularKeywords?
                            "Switching to Popular Keywords will clear your current keyword filters.":
                            "Switching to All Keywords will clear your current keyword filters.",
                        "location-section"
                    ).then(() => {
                        this.dropAllSelectedKeywords();
                        this.popularKeysTabSelected = isPopularKeywords;
                        if (!this.popularKeywords?.length) this.loadPopularKeyWords();
                    }).catch((err) => { });

                setTimeout(() => {
                    document.querySelector('.dialog_overlay.location-section').scrollIntoView({
                        behavior: 'smooth'
                    });
                })
            } else {
                this.popularKeysTabSelected = isPopularKeywords;
                if (!this.popularKeywords?.length) this.loadPopularKeyWords();
            }
    }

    isKeywordsSelected(): boolean {
        return !!this.specialities.length ||
            !!this.specialities_any.length ||
            !!this.specialities_all.length ||
            !!this.specialities_none.length ||
            !!this.specialities_popular.length;
    }

    dropAllSelectedKeywords(): void {
        this.specialities = [];
        this.specialities_any = [];
        this.specialities_all = [];
        this.specialities_none = [];
        this.specialities_popular = [];
        this.data.specialties = [];
        this.data.specialties_any = [];
        this.data.specialties_all = [];
        this.data.specialties_none = [];
        this.data.popular_keywords = [];
    }

}
